import { useState, useRef, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import LanguageSwitcher from './LanguageSwitcher';
import logo from '../../assests/ampera_logo.png';
import gsap from 'gsap';

export default function Header() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, profile, signOut, isMedia, isInvestor, isCustomer, isAdmin } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  // Refs for mobile menu animation
  const mobileMenuRef = useRef(null);
  const mobileMenuItemsRef = useRef([]);

  // Refs for user dropdown animation
  const userDropdownRef = useRef(null);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Animate mobile menu
  useEffect(() => {
    const menu = mobileMenuRef.current;
    const menuItems = mobileMenuItemsRef.current;

    if (mobileMenuOpen) {
      // Animate menu open
      if (menu) {
        gsap.fromTo(
          menu,
          { height: 0, opacity: 0 },
          { height: 'auto', opacity: 1, duration: 0.4, ease: 'power3.out' }
        );
      }

      // Stagger animate menu items
      if (menuItems.length > 0) {
        gsap.fromTo(
          menuItems.filter(Boolean),
          { opacity: 0, x: -20 },
          { opacity: 1, x: 0, duration: 0.3, stagger: 0.05, delay: 0.1, ease: 'power2.out' }
        );
      }
    } else {
      // Animate menu close
      if (menu) {
        gsap.to(menu, {
          height: 0,
          opacity: 0,
          duration: 0.3,
          ease: 'power3.in',
        });
      }
    }
  }, [mobileMenuOpen]);

  // Animate user dropdown
  useEffect(() => {
    const dropdown = userDropdownRef.current;

    if (userMenuOpen) {
      if (dropdown) {
        gsap.set(dropdown, { display: 'block' });
        gsap.fromTo(
          dropdown,
          { opacity: 0, scale: 0.95, y: -10 },
          { opacity: 1, scale: 1, y: 0, duration: 0.2, ease: 'power2.out' }
        );
        // Animate children
        gsap.fromTo(
          dropdown.children,
          { opacity: 0, y: -5 },
          { opacity: 1, y: 0, duration: 0.2, stagger: 0.03, delay: 0.05, ease: 'power2.out' }
        );
      }
    } else {
      if (dropdown) {
        gsap.to(dropdown, {
          opacity: 0,
          scale: 0.95,
          y: -10,
          duration: 0.15,
          ease: 'power2.in',
          onComplete: () => {
            gsap.set(dropdown, { display: 'none' });
          },
        });
      }
    }
  }, [userMenuOpen]);

  const navLinks = [
    { to: '/', label: t('nav.home') },
    { to: '/brand', label: t('nav.brand') },
    { to: '/branches', label: t('nav.branches') },
    { to: '/menu', label: t('nav.menu') },
    { to: '/catering', label: t('nav.catering') },
    { to: '/nasi-dus', label: t('nav.nasiDus') },
    { to: '/careers', label: t('nav.careers') },
    { to: '/news', label: t('nav.news') },
    { to: '/contact', label: t('nav.contact') },
  ];

  const linkClass = ({ isActive }) =>
    `px-3 py-2 rounded-lg transition-colors duration-200 ${
      isActive
        ? 'text-primary-600 bg-primary-50 font-medium'
        : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
    }`;

  // Get user's portal link based on role (for special access)
  const getPortalInfo = () => {
    if (isAdmin) return { link: '/admin', label: t('auth.adminPanel') };
    if (isMedia) return { link: '/admin', label: t('auth.mediaPanel') };
    if (isInvestor) return { link: '/investor', label: t('auth.investorPortal') };
    if (isCustomer) return { link: '/portal', label: t('auth.customerPortal') };
    return null;
  };

  const portalInfo = getPortalInfo();

  const handleSignOut = async () => {
    await signOut();
    setUserMenuOpen(false);
    navigate('/');
  };

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img src={logo} alt="Ampera" className="h-10 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            {navLinks.map((link) => (
              <NavLink key={link.to} to={link.to} className={linkClass} end={link.to === '/'}>
                {link.label}
              </NavLink>
            ))}
          </nav>

          {/* Right Side: Auth + Language */}
          <div className="flex items-center space-x-3">
            <LanguageSwitcher />

            {/* Auth Section */}
            {isAuthenticated ? (
              /* User Menu */
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="text-primary-600 font-medium text-sm">
                      {profile?.full_name?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  </div>
                  <span className="hidden sm:block text-sm font-medium text-gray-700 max-w-[100px] truncate">
                    {profile?.full_name?.split(' ')[0] || t('auth.user')}
                  </span>
                  <svg
                    className={`w-4 h-4 text-gray-500 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Animated Dropdown Menu */}
                <div
                  ref={userDropdownRef}
                  className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50 origin-top-right"
                  style={{ display: 'none' }}
                >
                  {/* User Info */}
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="font-medium text-gray-900 truncate">{profile?.full_name}</p>
                    <p className="text-sm text-gray-500 truncate">{profile?.email || profile?.phone}</p>
                    <span className="inline-block mt-1 px-2 py-0.5 bg-primary-100 text-primary-700 text-xs rounded-full capitalize">
                      {profile?.role}
                    </span>
                  </div>

                  {/* Menu Items */}
                  <div className="py-2">
                    <Link
                      to="/account"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      {t('auth.myAccount')}
                    </Link>
                    {portalInfo && (
                      <Link
                        to={portalInfo.link}
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        {portalInfo.label}
                      </Link>
                    )}
                  </div>

                  {/* Sign Out */}
                  <div className="border-t border-gray-100 pt-2">
                    <button
                      onClick={handleSignOut}
                      className="block w-full text-left px-4 py-2 text-red-600 hover:bg-red-50 transition-colors"
                    >
                      {t('auth.signOut')}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              /* Login Button */
              <Link
                to="/login"
                className="hidden sm:inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
              >
                {t('auth.signIn')}
              </Link>
            )}

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation - Animated */}
        <nav
          ref={mobileMenuRef}
          className="md:hidden overflow-hidden border-t border-gray-100"
          style={{ height: 0, opacity: 0 }}
        >
          <div className="py-4 flex flex-col space-y-1">
            {navLinks.map((link, index) => (
              <NavLink
                key={link.to}
                ref={(el) => (mobileMenuItemsRef.current[index] = el)}
                to={link.to}
                className={linkClass}
                end={link.to === '/'}
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </NavLink>
            ))}

            {/* Mobile Auth Links */}
            <div
              ref={(el) => (mobileMenuItemsRef.current[navLinks.length] = el)}
              className="pt-4 mt-4 border-t border-gray-100"
            >
              {isAuthenticated ? (
                <>
                  <Link
                    to="/account"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block px-3 py-2 text-primary-600 font-medium rounded-lg hover:bg-primary-50"
                  >
                    {t('auth.myAccount')}
                  </Link>
                  {portalInfo && (
                    <Link
                      to={portalInfo.link}
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-3 py-2 text-gray-700 rounded-lg hover:bg-gray-50"
                    >
                      {portalInfo.label}
                    </Link>
                  )}
                  <button
                    onClick={() => {
                      handleSignOut();
                      setMobileMenuOpen(false);
                    }}
                    className="block w-full text-left px-3 py-2 text-red-600 rounded-lg hover:bg-red-50"
                  >
                    {t('auth.signOut')}
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-2 text-primary-600 font-medium rounded-lg hover:bg-primary-50"
                >
                  {t('auth.signIn')}
                </Link>
              )}
            </div>
          </div>
        </nav>
      </div>
    </header>
  );
}
